/** Strings owned by `src/features/vitality/` — WP-02's Vitality page
 * (7 cards, filters, static HR-action suggestions). */
export const vitality = {
  page: {
    title: 'Vitality',
    description: 'Anonymized, indicative wellbeing trends at group level.',
    noScope: 'Select a company to see its vitality numbers.',
    noDepartments: 'No department here has statistics you may see.',
    /**
     * Rendered through `<Trans>`, with the two key phrases wrapped in
     * literal `<strong>` tags rather than numbered `<0>`/`<1>` placeholders.
     * `react-i18next` (`transKeepBasicHtmlNodesFor`, default includes
     * `strong`) matches a literal tag name to the Nth same-tag element in
     * `<Trans>`'s JSX children by occurrence, which stays correct regardless
     * of how much literal text sits before/between the tags. Numbered
     * placeholders instead index straight into `<Trans>`'s *raw* top-level
     * children array — text nodes and all — so a translation like "All
     * figures are <0>...</0>" would resolve `<0>` to the leading text child
     * ("All figures are "), not the first `<strong>`; verified against
     * `shell.configWarning.body`, which has exactly this bug (its `<1>`/`<2>`
     * resolve to a text node and the `{{missing}}` value child instead of
     * its `<code>` elements) — not this WP's to fix, but not a pattern to
     * copy either.
     */
    disclaimer:
      'All figures are <strong>indicative well-being insights</strong> and are <strong>not intended as a medical diagnosis</strong>. Trends are only shown for a sufficient number of participants (privacy by design).',
  },
  filters: {
    title: 'Filters',
    department: {
      label: 'Department',
    },
    location: {
      label: 'Location',
      all: 'All locations',
    },
  },
  badge: {
    measured: 'Measured',
    aiDerived: 'AI insight',
  },
  level: {
    good: 'Good',
    opportunity: 'Opportunity',
  },
  statusPill: {
    healthy: 'Healthy level',
    opportunity: 'Chance to help',
    strongForward: 'Strong & climbing',
  },
  trend: {
    flat: 'No change',
    vsPrevious: 'vs previous period',
    vsAverage: 'vs period average',
    up: 'Climbing',
    down: 'Slightly lower',
    upBad: 'Slightly higher',
    week: 'wk',
  },
  units: {
    hoursMinutes: '{{h}}h {{m}}m',
    average: 'avg. {{value}}',
  },
  status: {
    noData: 'No data in this range.',
  },
  table: {
    level: 'Level',
    trend: 'Trend',
    action: 'Action',
    actionNone: '—',
    actionLink: 'Department',
    actionLinkTitle: 'Go to this department',
  },
  insight: {
    title: 'What the vitality numbers say',
    unavailable: 'Not enough data yet for a summary.',
    vitality: {
      up: 'Vitality is climbing ({{pct}}% {{period}}) at a participation rate of {{participation}}%.',
      down: 'Vitality is declining ({{pct}}% {{period}}) at a participation rate of {{participation}}%.',
      flat: 'Vitality is stable ({{pct}}% {{period}}) at a participation rate of {{participation}}%.',
      noParticipation: 'Vitality is stable ({{pct}}% {{period}}).',
    },
    period: {
      last7Days: 'in the last 7 days',
      last30Days: 'this month',
      thisWeek: 'this week',
      thisMonth: 'this month',
      custom: 'this period',
    },
    stress: {
      up: 'Stress is climbing ({{pct}}%)',
      down: 'stress is easing ({{pct}}%)',
      flat: 'stress is holding steady',
    },
    sleep: {
      longer: 'and employees are sleeping {{minutes}} min longer on average.',
      shorter: 'and employees are sleeping {{minutes}} min less on average.',
      same: 'and employees are sleeping about the same amount.',
    },
    mostActiveDay: '{{day}} is the most active day.',
  },
  cards: {
    vitalityScore: {
      title: 'Vitality score',
      unit: '%',
      info: 'A composite score based on sleep, activity, HRV, stress and resting heart rate.',
    },
    sleep: {
      title: 'Sleep',
      unit: '',
      info: 'Average sleep duration per night.',
    },
    stress: {
      title: 'Stress',
      unit: '%',
      info: 'Measured stress level; the lower, the calmer.',
    },
    recovery: {
      title: 'Recovery',
      unit: '%',
      info: 'How well the body recovers, based on sleep, HRV and stress.',
    },
    activity: {
      title: 'Activity',
      unit: 'steps/day',
      info: 'Average number of steps per day.',
    },
    hrv: {
      title: 'HRV',
      unit: 'ms',
      info: 'Heart rate variability — an indicator of recovery and resilience.',
    },
    restingHeartRate: {
      title: 'Resting heart rate',
      unit: 'bpm',
      info: 'The lowest measured heart rate in the selected period, as an approximation of resting heart rate.',
    },
  },
  actions: {
    vitalityScore: {
      good: 'Vitality is trending well — keep the current routines in place.',
      opportunity:
        'Vitality has room to grow — a short team check-in can surface what is getting in the way.',
    },
    sleep: {
      good: 'Sleep looks healthy across the team — nothing to act on.',
      opportunity:
        'Sleep is below target — consider a reminder about wind-down routines and screen time before bed.',
    },
    stress: {
      good: 'Stress levels are under control — keep monitoring.',
      opportunity:
        'Stress is elevated — a short workload check-in or a recovery-focused activity could help.',
    },
    recovery: {
      good: 'Recovery is solid — the team is bouncing back well between efforts.',
      opportunity: 'Recovery is lagging — look at whether rest days and workload are balanced.',
    },
    activity: {
      good: 'Activity levels are healthy — no action needed.',
      opportunity:
        'Activity is below target — a walking-meeting nudge or a step challenge could help.',
    },
    hrv: {
      good: 'HRV looks healthy — no action needed.',
      opportunity:
        'HRV is lower than expected — this often tracks with stress or poor sleep; worth a closer look together.',
    },
    restingHeartRate: {
      good: 'Resting heart rate is in a healthy range.',
      opportunity:
        'Resting heart rate is elevated — combined with stress or sleep dips, this is worth flagging.',
    },
  },
} as const;
